import * as SS from "styled-system";
import palette from "../palette";

const baseSizes = {
  px: "1px",
  "0": "0",
  "1": "0.25rem",
  "2": "0.5rem",
  "3": "0.75rem",
  "4": "1rem",
  "5": "1.25rem",
  "6": "1.5rem",
  "8": "2rem",
  "10": "2.5rem",
  "12": "3rem",
  "16": "4rem",
  "20": "5rem",
  "24": "6rem",
  "32": "8rem",
  "40": "10rem",
  "48": "12rem",
  "56": "14rem",
  "64": "16rem",
};

const largeSizes = {
  full: "100%",
  "3xs": "14rem",
  "2xs": "16rem",
  xs: "20rem",
  sm: "24rem",
  md: "28rem",
  lg: "32rem",
  xl: "36rem",
  "2xl": "42rem",
  "3xl": "48rem",
  "4xl": "56rem",
  "5xl": "64rem",
  "6xl": "72rem",
};

const containers = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1224px",
};

const sizes = {
  ...baseSizes,
  ...largeSizes,
  containers,
};

const typography = {
  letterSpacings: {
    tighter: "-0.05em",
    tight: "-0.025em",
    normal: "0",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em",
  },
  lineHeights: {
    normal: "normal",
    none: "1",
    shorter: "1.2",
    short: "1.275",
    base: "1.333",
    tall: "1.5",
    taller: "1.75",
  },
  fontWeights: {
    hairline: 100,
    thin: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },
  fonts: {
    body: '"proxima-nova", Helvetica, Arial, sans-serif',
  },
  fontSizes: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "2rem",
    "4xl": "2.25rem",
    "5xl": "3rem",
    "6xl": "4rem",
  },
};

const zIndices = {
  hide: -1,
  auto: "auto",
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  typeahead: 1450,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
};

const radii = {
  none: "0",
  sm: "0.125rem",
  md: "0.25rem",
  lg: "0.5rem",
  xl: "0.75rem",
  "2xl": "1.25rem",
  full: "9999px",
};

const borders = {
  none: 0,
  "1px": "1px solid",
  "2px": "2px solid",
  "4px": "4px solid",
};

type Breakpoints = string[] | { sm: string; md: string; lg: string; xl: string };

const breakpoints: any = ["30em", "48em", "62em", "76.5em"];
const [sm, md, lg, xl] = breakpoints;

// aliases
breakpoints.sm = sm;
breakpoints.md = md;
breakpoints.lg = lg;
breakpoints.xl = xl;

const shadows = {
  sm: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  outline: "0 0 0 3px rgba(66, 153, 225, 0.6)",
  inner: "inset 0 2px 4px 0 rgba(0,0,0,0.06)",
  none: "none",
};

const theme: IDefaultTheme = {
  breakpoints,
  zIndices,
  radii,
  shadows,
  borders,
  colors: palette,
  ...typography,
  sizes,
  space: baseSizes,
};

export default theme;

type StringOrNumber = string | number;

interface IZIndices {
  hide: number;
  auto: string;
  base: number;
  docked: number;
  dropdown: number;
  sticky: number;
  banner: number;
  overlay: number;
  modal: number;
  popover: number;
  skipLink: number;
  toast: number;
  tooltip: number;
  typeahead: number;
}

interface IRadii {
  none: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  "2xl": string;
  full: string;
}

interface IBorders {
  none: StringOrNumber;
  "1px": StringOrNumber;
  "2px": StringOrNumber;
  "4px": StringOrNumber;
}

interface IColorHues {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

interface IColors {
  transparent: string;
  current: string;
  black: string;
  white: string;
  whiteAlpha: IColorHues;
  gray: IColorHues;
  red: IColorHues;
  orange: IColorHues;
  teal: IColorHues;
  blue: IColorHues;
  cyan: IColorHues;
}

export type VariantColor = keyof Omit<IColors, "transparent" | "current">;

interface IBaseSizes {
  px: string;
  "0": string;
  "1": string;
  "2": string;
  "3": string;
  "4": string;
  "5": string;
  "6": string;
  "8": string;
  "10": string;
  "12": string;
  "16": string;
  "20": string;
  "24": string;
  "32": string;
  "40": string;
  "48": string;
  "56": string;
  "64": string;
}

interface ILargeSizes {
  full: string;
  "3xs": string;
  "2xs": string;
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  "2xl": string;
  "3xl": string;
  "4xl": string;
  "5xl": string;
  "6xl": string;
}

interface IContainers {
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

type Sizes = IBaseSizes &
  ILargeSizes & {
    containers: IContainers;
  };

interface ILetterSpacings {
  tighter: string;
  tight: string;
  normal: string;
  wide: string;
  wider: string;
  widest: string;
}

interface ILineHeights {
  normal: string;
  none: string;
  shorter: string;
  short: string;
  base: string;
  tall: string;
  taller: string;
}

interface IFontWeights {
  hairline: number;
  thin: number;
  light: number;
  normal: number;
  medium: number;
  semibold: number;
  bold: number;
  extrabold: number;
  black: number;
}

interface IFonts {
  body: string;
}

interface IFontSizes {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  "2xl": string;
  "3xl": string;
  "4xl": string;
  "5xl": string;
  "6xl": string;
}

interface ITypography {
  letterSpacings: ILetterSpacings;
  lineHeights: ILineHeights;
  fontWeights: IFontWeights;
  fonts: IFonts;
  fontSizes: IFontSizes;
}

interface IShadows {
  sm: string;
  md: string;
  lg: string;
  xl: string;
  "2xl": string;
  outline: string;
  inner: string;
  none: string;
}

export interface IDefaultTheme extends ITypography {
  breakpoints: Breakpoints;
  zIndices: IZIndices;
  radii: IRadii;
  shadows: IShadows;
  borders: IBorders;
  colors: IColors;
  sizes: Sizes;
  space: IBaseSizes;
}

export type ICustomTheme = SS.Theme;

export type ITheme = IDefaultTheme;
